import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Users,
  Zap,
  Globe,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
} from 'lucide-react';
import { cn } from '../lib/utils';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Landing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'ar';

  const features = [
    {
      icon: <FileText className="h-6 w-6" />,
      title: lang === 'ar' ? 'مستندات غنية' : 'Rich Documents',
      description: lang === 'ar' 
        ? 'محرر بلوك قوي مع دعم للوسائط والجداول والكود'
        : 'Powerful block editor with media, tables, and code support',
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: lang === 'ar' ? 'تعاون فوري' : 'Real-time Collaboration',
      description: lang === 'ar'
        ? 'اعمل مع فريقك في الوقت الفعلي مع مؤشرات مباشرة'
        : 'Work with your team in real-time with live cursors',
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: lang === 'ar' ? 'مدعوم بالذكاء الاصطناعي' : 'AI-Powered',
      description: lang === 'ar'
        ? 'كتابة وترجمة وتلخيص ذكي باستخدام Gemini'
        : 'Smart writing, translation, and summarization with Gemini',
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: lang === 'ar' ? 'دعم كامل للعربية' : 'Full Arabic Support',
      description: lang === 'ar'
        ? 'واجهة RTL كاملة مع ترجمة عربية'
        : 'Complete RTL interface with Arabic translations',
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      title: lang === 'ar' ? 'إدارة المهام' : 'Task Management',
      description: lang === 'ar'
        ? 'لوحة كانبان وقوائم مهام مدمجة في مستنداتك'
        : 'Kanban boards and to-do lists integrated into your docs',
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: lang === 'ar' ? 'آمن وخاص' : 'Secure & Private',
      description: lang === 'ar'
        ? 'تشفير من طرف لطرف مع تحكم كامل في البيانات'
        : 'End-to-end security with full data control',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold">
                M
              </div>
              <span className="text-xl font-bold">MRSpace</span>
            </div>
            
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="inline" />
              <Link
                to="/login"
                className="text-sm font-medium hover:text-primary transition-colors"
              >
                {lang === 'ar' ? 'تسجيل الدخول' : 'Log in'}
              </Link>
              <Link
                to="/register"
                className="btn-primary"
              >
                {lang === 'ar' ? 'ابدأ مجاناً' : 'Get Started Free'}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
            <Sparkles className="h-4 w-4" />
            {lang === 'ar' ? 'مدعوم بالذكاء الاصطناعي' : 'Powered by AI'}
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            {lang === 'ar' ? (
              <>
                مساحة عملك
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600"> الذكية</span>
              </>
            ) : (
              <>
                Your
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600"> Smart </span>
                Workspace
              </>
            )}
          </h1>
          
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            {lang === 'ar'
              ? 'دوّن أفكارك، نظّم مشاريعك، وتعاون مع فريقك في مكان واحد. مع دعم كامل للغة العربية والذكاء الاصطناعي.'
              : 'Write, plan, and collaborate in one place. With full Arabic support and AI-powered features to boost your productivity.'}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="btn-primary h-12 px-8 text-base gap-2 group"
            >
              {lang === 'ar' ? 'ابدأ مجاناً' : 'Start for Free'}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform flip-rtl" />
            </Link>
            <Link
              to="/login"
              className="btn-ghost h-12 px-8 text-base border"
            >
              {lang === 'ar' ? 'لدي حساب بالفعل' : 'I have an account'}
            </Link>
          </div>
          
          {/* Trust badges */}
          <div className="flex items-center justify-center gap-8 mt-12 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {lang === 'ar' ? 'إعداد في دقيقة' : 'Setup in 1 minute'}
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              {lang === 'ar' ? 'آمن 100%' : '100% Secure'}
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              {lang === 'ar' ? 'سريع جداً' : 'Lightning Fast'}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {lang === 'ar' ? 'كل ما تحتاجه في مكان واحد' : 'Everything you need in one place'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {lang === 'ar'
                ? 'أدوات قوية لمساعدتك على الكتابة والتخطيط والتعاون بشكل أفضل'
                : 'Powerful tools to help you write, plan, and collaborate better'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div
                key={i}
                className={cn(
                  'p-6 rounded-2xl bg-card border shadow-sm hover:shadow-md transition-all',
                  'hover:border-primary/50 hover:-translate-y-1'
                )}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {lang === 'ar' ? 'جاهز للبدء؟' : 'Ready to get started?'}
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            {lang === 'ar'
              ? 'انضم لآلاف المستخدمين الذين يعملون بشكل أذكى مع MRSpace'
              : 'Join thousands of users working smarter with MRSpace'}
          </p>
          <Link
            to="/register"
            className="btn-primary h-12 px-8 text-base"
          >
            {lang === 'ar' ? 'أنشئ حسابك المجاني' : 'Create your free account'}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                M
              </div>
              <span className="font-semibold">MRSpace</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 MRSpace. {lang === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
